import React, { useContext, useRef, useEffect, useState } from 'react'
import { AuthenticationContext } from '../../Auth/authentication.context'
import { useBranding } from '../../contexts/BrandingContext'
import {
  Label,
  Form,
  FormFeedback,
  Input,
  InputGroup,
  InputGroupText,
} from 'reactstrap'

import { Link, Navigate, useNavigate } from 'react-router-dom'
import { SelectButton } from 'primereact/selectbutton'

// Formik validation
import * as Yup from 'yup'
import { useFormik } from 'formik'
import { Toast } from 'primereact/toast'

import AuthFormsLayout from '../Layout/AuthFormsLayout'
import { Row, Col, CardBody, Card, Container } from 'reactstrap'

import { ToastPopUp } from '../shared/utils'

import AuthLayout from '../Layout/AuthLayout'
import Modals from '../shared/Modals'

const Login = (props) => {
  const [showPassword, setShowPassword] = useState(false)
  const [showSystemModal, setShowSystemModal] = useState(false)
  const [selectedSystem, setSelectedSystem] = useState(null)
  const navigate = useNavigate()
  const { isGroupMode, displayName, domainForLogin } = useBranding()

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  const { onLogin, user, isAdmin, error, toastStatus } = useContext(
    AuthenticationContext
  )

  const toast = useRef(null)

  useEffect(() => {
    if (toastStatus.toastStatus !== undefined) {
      ToastPopUp(
        toast,
        toastStatus?.toastStatus,
        toastStatus?.summary,
        toastStatus?.msg
      )
    }
  }, [toastStatus])

  // Show system selection modal when user logs in successfully
  useEffect(() => {
    const currentEmail = user?.email
    // Check for user-specific preference first, fall back to general preference
    const userSpecificSkip = currentEmail
      ? localStorage.getItem(`showSystemModal_${currentEmail}`) === 'false'
      : false
    const generalSkip = localStorage.getItem('showSystemModal') === 'false'
    const skipSystemModal = userSpecificSkip || generalSkip

    const lastEmail = localStorage.getItem('lastLoginEmail')
    const isDifferentUser =
      lastEmail !== currentEmail && lastEmail && currentEmail

    // Store the current user's email if logged in
    if (user && user.email) {
      localStorage.setItem('lastLoginEmail', user.email)
    }

    // Always show modal for different users with multiple modules
    if (
      user &&
      user.pharmacyModules &&
      user.pharmacyModules.length > 1 &&
      isDifferentUser
    ) {
      setShowSystemModal(true)
    }
    // Show modal if criteria met but not specifically due to different user
    else if (
      user &&
      user.pharmacyModules &&
      user.pharmacyModules.length > 1 &&
      !skipSystemModal &&
      (!lastEmail || lastEmail === currentEmail)
    ) {
      setShowSystemModal(true)
    } else if (
      user &&
      user.pharmacyModules &&
      user.pharmacyModules.length === 1
    ) {
      const moduleName = isGroupMode ? 'Origins Management' : user.pharmacyModules[0].name
      localStorage.setItem('module', moduleName)
      navigate('/')
    } else if (
      user &&
      skipSystemModal &&
      user.pharmacyModules &&
      user.pharmacyModules.length > 0
    ) {
      const moduleName = isGroupMode ? 'Origins Management' : user.pharmacyModules[0].name
      localStorage.setItem('module', moduleName)
      navigate('/')
    }
  }, [user, navigate, isGroupMode])

  useEffect(() => {
    if (user && user.pharmacyModules && user.pharmacyModules.length > 0) {
      const moduleName = isGroupMode ? 'Origins Management' : user.pharmacyModules[0].name
      localStorage.setItem('module', moduleName)
      navigate('/')
    }
  }, [user, navigate, isGroupMode])

  const handleDoNotShowAgain = () => {
    localStorage.setItem('showSystemModal', 'false')
    navigate('/')
  }

  const handleSystemSelect = (system) => {
    localStorage.setItem('module', system)
    setSelectedSystem(system)
    setShowSystemModal(false)
    navigate('/')
  }

  const validation = useFormik({
    initialValues: {
      email: '',
      password: '',
      accessCode: '',
    },
    enableReinitialize: false,
    validationSchema: Yup.object({
      email: Yup.string().required('Please Enter Your account Number or email'),
      password: Yup.string().min(8).required('Please Enter Your Password'),
      ...(isGroupMode && {
        accessCode: Yup.string().required('Access code is required'),
      }),
    }),
    onSubmit: (values) => {
      onLogin(
        values.email,
        values.password,
        'pharmacy',
        undefined,
        isGroupMode ? values.accessCode : undefined,
        isGroupMode ? domainForLogin : undefined
      )
    },
  })

  document.title = 'Login'

  const options = ['Pharmacy', 'Manufacturer']
  const [value, setValue] = useState(options[0])

  return (
    <>
      <AuthLayout>
        <Toast ref={toast} />
        {!user ? (
          <>
            <Card className='p-4'>
              <h2>Let's log you in {isGroupMode ? displayName : 'Origins Direct'}</h2>
              <h3 className='text-center'>Login as a Pharmacy</h3>
              <Form
                className='mt-4'
                onSubmit={(e) => {
                  e.preventDefault()
                  validation.handleSubmit()
                  return false
                }}
                action='#'
                noValidate
              >
                <div className='mb-3'>
                  <Label className='form-label' htmlFor='email'>
                    Account # or email
                  </Label>
                  <Input
                    name='email'
                    className='form-control'
                    placeholder='Enter account number or email'
                    type='text'
                    id='email'
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.email || ''}
                    invalid={
                      validation.touched.email && validation.errors.email
                    }
                  />
                  {validation.touched.email && validation.errors.email ? (
                    <FormFeedback type='invalid'>
                      {validation.errors.email}
                    </FormFeedback>
                  ) : null}
                </div>

                <div className='mb-3'>
                  <InputGroup>
                    <Input
                      name='password'
                      value={validation.values.password || ''}
                      type={showPassword ? 'text' : 'password'}
                      className='form-control'
                      placeholder='Enter Password'
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      invalid={
                        validation.touched.password &&
                        !!validation.errors.password
                      }
                    />
                    <InputGroupText
                      onClick={togglePasswordVisibility}
                      style={{ cursor: 'pointer' }}
                    >
                      <i
                        className={`mdi ${
                          showPassword
                            ? 'mdi-eye-off-outline'
                            : 'mdi-eye-outline'
                        }`}
                      ></i>
                    </InputGroupText>
                  </InputGroup>

                  {validation.touched.password && validation.errors.password ? (
                    <FormFeedback type='invalid'>
                      {validation.errors.password}
                    </FormFeedback>
                  ) : null}
                </div>

                {isGroupMode && (
                  <div className='mb-3'>
                    <Label className='form-label' htmlFor='accessCode'>
                      Access Code
                    </Label>
                    <Input
                      name='accessCode'
                      className='form-control'
                      placeholder='Enter access code'
                      type='text'
                      id='accessCode'
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.accessCode || ''}
                      invalid={
                        validation.touched.accessCode && validation.errors.accessCode
                      }
                    />
                    {validation.touched.accessCode && validation.errors.accessCode ? (
                      <FormFeedback type='invalid'>
                        {validation.errors.accessCode}
                      </FormFeedback>
                    ) : null}
                  </div>
                )}

                <div className='mb-3 row'>
                  <div className='col-sm-6'>
                    <div className='form-check'>
                      <input
                        type='checkbox'
                        className='form-check-input'
                        id='customControlInline'
                      />
                      <label
                        className='form-check-label'
                        htmlFor='customControlInline'
                      >
                        Remember me
                      </label>
                    </div>
                  </div>
                  <div className='col-sm-6 text-end'>
                    <button
                      className='btn btn-primary w-md waves-effect waves-light'
                      type='submit'
                    >
                      Log In
                    </button>
                  </div>
                </div>

                <div className='mt-2 mb-0 row'>
                  <div className='col-12 mt-4'>
                    <Link to='/forget-password'>
                      <i className='mdi mdi-lock'></i> Forget your password?
                    </Link>
                  </div>
                </div>
              </Form>

              {!isGroupMode && (
                <>
                  <div className='mt-4 text-center'>
                    <div className='line-divider'>
                      <div className='line'></div>
                      <span>Don't have an account ?</span>
                      <div className='line'></div>
                    </div>
                    <Link to='/register'>
                      <button className='create-account-btn'>Register Now</button>
                    </Link>
                  </div>

                  <div className='mt-4 d-flex gap-2 justify-content-center'>
                    <Link
                      to='/manufacturer-login'
                      className='fw-medium text-primary'
                    >
                      Login as a Manufacturer?
                    </Link>

                    <Link to='/admin-login' className='fw-medium text-primary'>
                      Origins Direct Team Login?
                    </Link>
                  </div>
                </>
              )}
            </Card>
          </>
        ) : null}
      </AuthLayout>

      {/* System Selection Modal */}
      {/* <Modals
        ModalHeader='Welcome to the Origins Systems Application Suite'
        btnName='Close'
        width='60rem'
        visible={showSystemModal}
        setVisible={setShowSystemModal}
        onButtonClick={() => {
          if (selectedSystem) {
            handleSystemSelect(selectedSystem)
          } else {
            setShowSystemModal(false)
            navigate('/')
          }
        }}
        disabled={selectedSystem}
        wait={true}
        onHide={() => navigate('/')}
        showDoNotShowAgain={true}
        onDoNotShowAgain={handleDoNotShowAgain}
        doNotShowAgainLabel="Don't show this welcome screen again"
        hideMainButton={false}
      >
        <div className='system-selection-container p-4'>
          <h4 className='text-center mb-4'>
            Connecting Industries, Empowering Efficiency
          </h4>

          <div className='row'>
          
            <div className='col-md-6 mb-3'>
              <div
                className={`system-option p-3 text-center h-100 ${
                  selectedSystem === 'Origins Direct Instant' ? 'selected' : ''
                }`}
                onClick={() => handleSystemSelect('Origins Direct Instant')}
                style={{
                  border:
                    selectedSystem === 'Origins Direct Instant'
                      ? '2px solid #626ED4'
                      : '1px solid #ddd',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                }}
              >
                <div className='mb-3'>
                  <div
                    className='system-logo d-flex align-items-center flex-column justify-content-center'
                    style={{
                      height: '100px',
                      width: '100px',
                      margin: '0 auto',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <i
                      className='mdi mdi-credit-card text-success'
                      style={{ fontSize: '50px' }}
                    ></i>
                    <div className=''>
                      <img
                        src={originsDirectInstant}
                        alt='Origins Instant'
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>
                </div>
                <p className='small text-muted'>
                  Origins Direct Instant is an extension of Origins Direct that
                  facilitates the financial aspect of medication procurement
                  from pharmaceutical manufacturers by retail pharmacies. It
                  allows retail pharmacies to drive growth and increase
                  profitability, while also improving cashflow for
                  manufacturers.
                </p>
              </div>
            </div>

           
            <div className='col-md-6 mb-3'>
              <div
                className={`system-option p-3 text-center h-100 ${
                  selectedSystem === 'Origins Direct' ? 'selected' : ''
                }`}
                onClick={() => handleSystemSelect('Origins Direct')}
                style={{
                  border:
                    selectedSystem === 'Origins Direct'
                      ? '2px solid #626ED4'
                      : '1px solid #ddd',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                }}
              >
                <div className='mb-3'>
                  <div
                    className='system-logo d-flex align-items-center flex-column justify-content-center'
                    style={{
                      height: '100px',
                      width: '100px',
                      margin: '0 auto',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <i
                      className='mdi mdi-shopping text-primary'
                      style={{ fontSize: '50px' }}
                    ></i>
                    <div className='system-logo-img'>
                      <img
                        src={originsDirect}
                        alt='Origins Direct'
                        style={{ width: '60%' }}
                      />
                    </div>
                  </div>
                </div>
                <p className='small text-muted'>
                  Origins Direct is a platform that connects retail pharmacies
                  with pharmaceutical manufacturers directly, creating an
                  essential link that combines the desires of the manufacturer
                  for effective distribution of their products with the needs of
                  the retail pharmacies for a steady, reliable source of a
                  variety of products.
                </p>
              </div>
            </div>

            
          
          </div>
        </div>
      </Modals> */}
    </>
  )
}

export default Login
